import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material'
import CardSectionHeader from 'src/v3/components/card-section-header'
import { ReactElement, useMemo, useState, useCallback } from 'react'
import { TextContent, StreamStep } from 'src/v3/core/types'
import { TextContentIcon } from 'src/v3/core/icons'
import { migrateVideo, migrateShowNotes, migrateQuiz } from '../../utils/api'
import {
  getStepIcon,
  getStepIconColor,
  getStepLabel,
} from '../utils/step-type-helpers'
import ContentItemCard from './content-item-card'
import {
  VideoLibraryItem,
  ShowNotesLibraryItem,
  QuizLibraryItem,
} from '../types'

type LegacyContent = {
  textContent: TextContent[]
}

type MigrationCallbacks = {
  onMigrateSuccess: (contentType: 'video' | 'showNotes' | 'quiz') => void
  onViewTextContent: (textContentID: string) => void
  onError: (message: string) => void
}

type MigratableContentListProps = {
  courseID: string
  steps: StreamStep[]
  courseVideos: VideoLibraryItem[]
  courseShowNotes: ShowNotesLibraryItem[]
  courseQuizzes: QuizLibraryItem[]
  legacyContent: LegacyContent
  isRefetching: boolean
  callbacks: MigrationCallbacks
  locked?: boolean
  onUnpublish?: () => void
  unpublishDisabled?: boolean
}

type ContentItem = {
  id: string
  type: 'video' | 'showNotes' | 'quiz' | 'textContent'
  name: string
  stepIndex?: number
  streamStepID?: string
}

export default function MigratableContentList({
  courseID,
  steps,
  courseVideos,
  courseShowNotes,
  courseQuizzes,
  legacyContent,
  isRefetching,
  callbacks,
  locked,
  onUnpublish,
  unpublishDisabled,
}: MigratableContentListProps): ReactElement | null {
  let { textContent: legacyTextContent } = legacyContent
  let { onMigrateSuccess, onViewTextContent, onError } = callbacks
  let [migratingItemID, setMigratingItemID] = useState<string | null>(null)

  let orderedItems = useMemo(() => {
    // All passed content is already filtered to isMigratable: true by the caller
    let videoMap = new Map(courseVideos.map((v) => [v.id, v]))
    let showNotesMap = new Map(courseShowNotes.map((sn) => [sn.id, sn]))
    let quizMap = new Map(courseQuizzes.map((q) => [q.id, q]))
    let textContentMap = new Map(legacyTextContent.map((tc) => [tc.id, tc]))

    // Content associated with steps (preserves step ordering)
    let steppedItems = steps
      .map((step): ContentItem | null => {
        if (step.videoID) {
          let video = videoMap.get(step.videoID)
          if (video) {
            return {
              id: video.id,
              type: 'video',
              name: video.name,
              stepIndex: step.stepIndex,
              streamStepID: step.id,
            }
          }
        } else if (step.showNotesID) {
          let note = showNotesMap.get(step.showNotesID)
          if (note) {
            return {
              id: note.id,
              type: 'showNotes',
              name: note.name,
              stepIndex: step.stepIndex,
              streamStepID: step.id,
            }
          }
        } else if (step.quizID) {
          let quiz = quizMap.get(step.quizID)
          if (quiz) {
            return {
              id: quiz.id,
              type: 'quiz',
              name: quiz.name,
              stepIndex: step.stepIndex,
              streamStepID: step.id,
            }
          }
        } else if (step.textContentID) {
          let tc = textContentMap.get(step.textContentID)
          if (tc) {
            return {
              id: tc.id,
              type: 'textContent',
              name: tc.name,
              stepIndex: step.stepIndex,
              streamStepID: step.id,
            }
          }
        }
        return null
      })
      .filter((item): item is ContentItem => item !== null)
      .toSorted((a, b) => (a.stepIndex ?? 0) - (b.stepIndex ?? 0))

    let steppedIDs = new Set(steppedItems.map((i) => i.id))

    // Content not associated with any step
    let unsteppedVideos = courseVideos
      .filter((v) => !steppedIDs.has(v.id))
      .map((v) => ({ id: v.id, type: 'video' as const, name: v.name }))

    let unsteppedShowNotes = courseShowNotes
      .filter((sn) => !steppedIDs.has(sn.id))
      .map((sn) => ({ id: sn.id, type: 'showNotes' as const, name: sn.name }))

    let unsteppedQuizzes = courseQuizzes
      .filter((q) => !steppedIDs.has(q.id))
      .map((q) => ({ id: q.id, type: 'quiz' as const, name: q.name }))

    let unsteppedTextContent = legacyTextContent
      .filter((tc) => !steppedIDs.has(tc.id))
      .map((tc) => ({ id: tc.id, type: 'textContent' as const, name: tc.name }))

    return [
      ...steppedItems,
      ...unsteppedVideos,
      ...unsteppedShowNotes,
      ...unsteppedQuizzes,
      ...unsteppedTextContent,
    ]
  }, [steps, courseVideos, courseShowNotes, courseQuizzes, legacyTextContent])

  let handleMigrateVideo = useCallback(
    (item: ContentItem) => {
      setMigratingItemID(item.id)
      migrateVideo({ contentID: item.id })
        .then(() => {
          onMigrateSuccess('video')
        })
        .catch((error) => {
          onError(
            error instanceof Error ? error.message : 'Failed to migrate video',
          )
        })
        .finally(() => {
          setMigratingItemID(null)
        })
    },
    [courseID, onMigrateSuccess, onError],
  )

  let handleMigrateShowNotes = useCallback(
    (item: ContentItem) => {
      setMigratingItemID(item.id)
      migrateShowNotes({ contentID: item.id, newCourseID: courseID })
        .then(() => {
          onMigrateSuccess('showNotes')
        })
        .catch((error) => {
          onError(
            error instanceof Error
              ? error.message
              : 'Failed to migrate show notes',
          )
        })
        .finally(() => {
          setMigratingItemID(null)
        })
    },
    [courseID, onMigrateSuccess, onError],
  )

  let handleMigrateQuiz = useCallback(
    (item: ContentItem) => {
      setMigratingItemID(item.id)
      migrateQuiz({
        contentID: item.id,
        newCourseID: courseID,
      })
        .then(() => {
          onMigrateSuccess('quiz')
        })
        .catch((error) => {
          onError(
            error instanceof Error ? error.message : 'Failed to migrate quiz',
          )
        })
        .finally(() => {
          setMigratingItemID(null)
        })
    },
    [courseID, onMigrateSuccess, onError],
  )

  let getItemConfig = (item: ContentItem) => {
    let isLoading = migratingItemID === item.id || isRefetching

    if (item.type === 'video') {
      return {
        itemID: item.id,
        itemName: item.name,
        icon: getStepIcon('video'),
        iconColor: getStepIconColor('video'),
        iconLabel: getStepLabel('video'),
        buttonText: 'Migrate Video',
        buttonAction: () => handleMigrateVideo(item),
        isLoading,
        disabled: !!locked || migratingItemID !== null,
        variant: 'outlined' as const,

        stepIndex: item.stepIndex,
      }
    }

    if (item.type === 'showNotes') {
      return {
        itemID: item.id,
        itemName: item.name,
        icon: getStepIcon('showNotes'),
        iconColor: getStepIconColor('showNotes'),
        iconLabel: getStepLabel('showNotes'),
        buttonText: 'Migrate Show Notes',
        buttonAction: () => handleMigrateShowNotes(item),
        isLoading,
        disabled: !!locked || migratingItemID !== null,
        variant: 'outlined' as const,

        stepIndex: item.stepIndex,
      }
    }

    if (item.type === 'quiz') {
      return {
        itemID: item.id,
        itemName: item.name,
        icon: getStepIcon('quiz'),
        iconColor: getStepIconColor('quiz'),
        iconLabel: getStepLabel('quiz'),
        buttonText: 'Migrate Quiz',
        buttonAction: () => handleMigrateQuiz(item),
        isLoading,
        disabled: !!locked || migratingItemID !== null,
        variant: 'outlined' as const,

        stepIndex: item.stepIndex,
      }
    }

    // textContent
    return {
      itemID: item.id,
      itemName: item.name,
      icon: <TextContentIcon />,
      iconColor: 'text.secondary',
      iconLabel: 'Text Content',
      buttonText: 'View Text Content',
      buttonAction: () => onViewTextContent(item.id),
      isLoading: isRefetching,
      variant: 'outlined' as const,
      stepIndex: item.stepIndex,
    }
  }

  if (orderedItems.length === 0) {
    return null
  }

  return (
    <Card className="mt-6" sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <CardSectionHeader
          label="Legacy Content Migration"
          component="h3"
          className="mb-4"
        />

        {locked ? (
          <Alert
            severity="warning"
            className="mb-4"
            action={
              onUnpublish && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onUnpublish}
                  disabled={unpublishDisabled}
                >
                  Unpublish
                </Button>
              )
            }
          >
            This course is published with legacy content. Unpublish the course
            before migrating content.
          </Alert>
        ) : (
          <Alert severity="info" className="mb-4">
            <Typography variant="body2">
              This course has legacy content that needs to be migrated to the
              new Course V5 format. Videos, Show Notes, and Quizzes can be
              automatically migrated. Text content needs to be manually
              recreated as Show Notes.
            </Typography>
          </Alert>
        )}

        <Box component="ul" className="flex flex-col gap-2 p-0 m-0 list-none">
          {orderedItems.map((item) => (
            <ContentItemCard key={item.id} {...getItemConfig(item)} />
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}
